import { useEffect, useMemo, useRef, useState } from 'react'
import uPlot from 'uplot'
import { stackValues, stackGroups, clampWindow, zoomWindow, nearestIndex, PLOT_PAD_RIGHT, X_AXIS_H, bandPairs, outsideIndices, bandSegments, lineSeriesOptions, bandHit, dragBand, BAND_EDGE_PX } from './model.js'
import { columnPaths, devicePixelRatioOf } from './paths.js'
import { makeResolver, watchTheme } from './theme.js'
import { axisTimeValues, axisTimeValuesIn } from './format.js'
import { edgeSafe } from './axis.js'

// DRAG_SLOP 은 「끌었다」로 볼 최소 이동 거리(px)다. 누를 때 손이 미세하게 흔들리는데,
// 그것까지 끌기로 보면 클릭이 아예 안 먹는다.
const DRAG_SLOP = 4
// 터치로 창 손잡이를 집을 폭(px). 손가락은 마우스 끝보다 굵다(model.js BAND_EDGE_PX).
const BAND_EDGE_TOUCH_PX = 16

// X_AXIS_H(model.js)는 x축 글자 줄의 높이다. height 는 **판 높이**라 축이 있으면 이만큼 더 커진다 —
// 축이 판 높이를 잡아먹으면 축이 있는 레인만 판이 낮아져, 같은 값의 막대가 레인마다 다른
// 높이로 선다.

// PAD 는 판 둘레의 여백이다. **고정값이어야 한다** — uPlot 은 오른쪽 여백을 마지막 축
// 글자 폭에 맞춰 자동으로 잡는데, 그러면 x축이 있는 레인만 판이 좁아져 레인끼리 x 가
// 어긋난다. 위·아래는 y축 끝 글자(100%·0%)의 반 높이가 잘리지 않을 만큼이고, x축이 있든
// 없든 같은 값이라 레인마다 판 높이가 같다. 오른쪽(PLOT_PAD_RIGHT)은 툴팁도 알아야 해서
// model.js 에 있다.
const PAD_Y = 8

// TimeChart 는 시간축 차트 한 판이다(uPlot 한 개).
//
// 데이터는 **열 단위**로 받는다 — { x: [ms…], cpu: [값…], … }. uPlot 이 그 모양을 그대로
// 먹어서 수만 점을 옮겨 담지 않는다. 시각은 epoch **ms** 다.
//
// 보는 구간(window)은 부르는 쪽이 들고 있다(제어 컴포넌트). 여러 레인이 같은 window 와
// syncKey 를 받으면 x축·줌·커서가 함께 움직인다 — 레인마다 따로 들면 한 시각에서 세로로
// 잘라 읽는 것이 성립하지 않는다.
//
// 자세한 사용법은 README.md.
export default function TimeChart(props) {
  const {
    data, series, bucketMs, height = 120, window: win, y = {}, xAxis = true, yAxis = true,
    yAxisSize = 48, syncKey, band, lines, empty, loading, ariaLabel, className,
  } = props
  const boxRef = useRef(null)
  const uRef = useRef(null)
  const propsRef = useRef(props)
  propsRef.current = props
  // 테마·밀도가 바뀐 횟수. 바뀌면 판을 새로 세운다 — 축 글꼴은 uPlot 이 만들 때 한 번만 읽어서,
  // 색만 다시 칠해서는 밀도(--f) 전환을 못 따라간다. 테마 전환은 드물어 다시 세워도 싸다.
  const [theme, setTheme] = useState(0)
  const [width, setWidth] = useState(0)
  // Shift + 끌기로 고르는 중인 구간. 확정 전까지 밴드를 미리 그린다.
  const selRef = useRef(null)

  // 쌓기는 여기서 한 번 한다 — uPlot 은 누적값을 받고, 각 조각의 바닥은 paths 가 따로 읽는다.
  const prepared = useMemo(() => {
    const values = series.map((s) => data[s.key] || [])
    const { tops, bases } = stackValues(series, values)
    const groupTop = new Array(series.length)
    for (const g of stackGroups(series)) {
      const last = tops[g.members[g.members.length - 1]]
      for (const m of g.members) groupTop[m] = last
    }
    return { rows: [data.x || [], ...tops], bases, groupTop }
  }, [data, series])
  const preparedRef = useRef(prepared)
  preparedRef.current = prepared

  // 판을 다시 세워야 하는 모양(시리즈 구성·축)만 모은 열쇠. 값·구간이 바뀌는 것은 여기 없다.
  // y 의 범위·눈금 **값**도 여기 없다 — 확대할 때마다 y 를 보이는 구간에 맞춰 다시 잡는 화면이
  // 있는데(store-mops 리소스 추이), 값이 열쇠에 있으면 휠 한 칸마다 레인 전부를 새로 세워
  // 커서가 끊기고 판이 깜박였다. 눈금을 손으로 주는가·글자 서식을 주는가(있고 없음)만 모양이다.
  const shapeKey = JSON.stringify([
    series.map((s) => [s.key, s.color, s.kind, s.stack, s.over, s.dash, s.outside]),
    props.bands || null,
    xAxis, yAxis, yAxisSize, syncKey, !!y.ticks, !!y.format, props.axisFont || null,
  ])

  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => watchTheme(() => setTheme((n) => n + 1)), [])

  useEffect(() => {
    const el = boxRef.current
    if (!el || width <= 0) return undefined
    const r = makeResolver(el)
    const u = new uPlot(buildOptions({ props: propsRef.current, preparedRef, propsRef, selRef, r, width }),
      preparedRef.current.rows, el.querySelector('.mops-chart__plot'))
    uRef.current = u
    const off = attachInteractions(u, propsRef, selRef)
    return () => {
      off()
      u.destroy()
      r.dispose()
      uRef.current = null
    }
    // width·height 는 아래 setSize 가 맡는다 — 여기 넣으면 창 크기를 바꿀 때마다 판을 새로 세운다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeKey, theme, width > 0])

  const total = height + (xAxis ? X_AXIS_H : 0)
  useEffect(() => { uRef.current?.setSize({ width, height: total }) }, [width, total])

  useEffect(() => {
    const u = uRef.current
    if (!u) return
    u.setData(prepared.rows)
  }, [prepared])

  useEffect(() => {
    const u = uRef.current
    if (!u || !win) return
    u.setScale('x', { min: win.from, max: win.to })
  }, [win?.from, win?.to])

  // y 범위·눈금이 바뀌면 판은 그대로 두고 y 만 다시 잡는다. 범위 함수(scales.y.range)는 지금 props 를
  // 읽으므로, 같은 자료로 setData 를 다시 부르면 uPlot 이 그 함수로 y 를 다시 잰다.
  const yKey = JSON.stringify([y.min, y.max, y.ticks])
  const yFirst = useRef(true)
  useEffect(() => {
    if (yFirst.current) { yFirst.current = false; return }
    uRef.current?.setData(preparedRef.current.rows)
  }, [yKey])

  // 밴드·기준선은 캔버스 위에 hooks.draw 가 그린다 — 바뀌면 다시 칠하기만 한다(경로는 그대로).
  // lines 는 부르는 쪽이 렌더마다 새 배열로 넘기기 쉽다 — 배열 자체를 보면 커서를 움직일
  // 때마다(툴팁 상태가 바뀔 때마다) 레인 전부를 다시 칠한다. 내용으로 본다.
  const linesKey = JSON.stringify(lines || null)
  useEffect(() => { uRef.current?.redraw(false) }, [band?.from, band?.to, linesKey, props.frame])

  const isEmpty = !loading && !(data.x && data.x.length)
  return (
    <div className={`mops-chart${props.onBandChange ? ' mops-chart--navigator' : ''}${className ? ` ${className}` : ''}`} ref={boxRef}
      role="img" aria-label={ariaLabel} style={{ minHeight: total }}>
      <div className="mops-chart__plot" />
      {/* 불러오는 중에는 「없다」고 말하지 않는다. 기간을 바꾸는 찰나에 「표본이 없습니다」가
          뜨면 사실이 아닌 것을 읽게 된다. */}
      {isEmpty && empty && <div className="mops-chart__empty">{empty}</div>}
    </div>
  )
}

function buildOptions({ props, preparedRef, propsRef, selRef, r, width }) {
  const { series, height = 120, xAxis = true, yAxis = true, yAxisSize = 48, syncKey, y = {}, timeZone, axisFont } = props
  const axisColor = r.color('--ink-muted')
  const gridColor = r.color('--hairline')
  const font = r.font(axisFont || '--t-mono-sm')
  // y 범위·눈금·서식은 **지금 props** 에서 읽는다 — 판을 다시 세우지 않고 y 만 바꾼다(위 yKey).
  const yNow = () => propsRef.current.y || {}
  const grid = { stroke: gridColor, width: 1 }
  const ticks = { stroke: gridColor, width: 1, size: 4 }
  const blank = (_u, splits) => splits.map(() => '')

  return {
    width,
    height: height + (xAxis ? X_AXIS_H : 0),
    padding: [PAD_Y, PLOT_PAD_RIGHT, PAD_Y, 0],
    // 시각을 ms 로 받는다(uPlot 기본은 초). 좌표 변환은 uPlot 이 JS number(float64)로 한다.
    ms: 1,
    // timeZone(IANA 이름)을 주면 눈금 자리(정시·자정)와 글자를 그 시간대로 잡는다. 안 주면 uPlot 기본 —
    // 브라우저 시간대다. 현장 PC 시계가 UTC 면 KST 화면이 9시간 밀려(dbix-mops#182 가 칸 번호로 우회) 더했다.
    ...(timeZone ? { tzDate: (ts) => uPlot.tzDate(new Date(ts), timeZone) } : {}),
    legend: { show: false },
    select: { show: false },
    cursor: {
      y: false,
      points: { show: false },
      // uPlot 기본 끌기(구간 확대)는 끈다 — 끌기는 이동, Shift + 끌기는 고르기로 attachInteractions 가 받는다.
      drag: { x: false, y: false, setScale: false },
      ...(syncKey ? { sync: { key: syncKey, setSeries: false } } : {}),
    },
    scales: {
      x: {
        time: true,
        // 보는 구간은 부르는 쪽이 정한다. 데이터가 새로 와도 구간이 제멋대로 전체로 풀리지 않게
        // 늘 지금 window 를 돌려준다.
        range: (_u, dmin, dmax) => {
          const w = propsRef.current.window
          return w && w.to > w.from ? [w.from, w.to] : [dmin, dmax]
        },
      },
      y: {
        range: (_u, dmin, dmax) => {
          const v = yNow()
          return [
            v.min ?? Math.min(0, dmin ?? 0),
            v.max ?? ((dmax ?? 0) > 0 ? dmax * 1.1 : 1),
          ]
        },
      },
    },
    axes: [
      xAxis
        ? { stroke: axisColor, font, grid, ticks, values: edgeSafe(timeZone ? axisTimeValuesIn(timeZone) : axisTimeValues), space: 90, size: X_AXIS_H }
        // 축 글자는 안 적되 세로 눈금선은 남긴다 — 레인 여럿이 맨 아래 축 하나를 같이 쓴다.
        : { stroke: axisColor, font, grid, ticks: { show: false }, values: blank, space: 90, size: 0 },
      {
        scale: 'y', stroke: axisColor, font, size: yAxisSize, gap: 4,
        grid: yAxis ? grid : { show: false },
        ticks: yAxis ? ticks : { show: false },
        values: yAxis ? (y.format ? (_u, s) => s.map((v) => (v == null ? '' : (yNow().format || String)(v))) : undefined) : blank,
        ...(y.ticks ? { splits: () => yNow().ticks || [] } : {}),
      },
    ],
    series: [{}, ...series.map((s, i) => seriesOptions(s, i, preparedRef, propsRef, r))],
    hooks: {
      // 띠는 계열보다 **먼저** 칠한다(drawClear — 판을 지운 직후). uPlot 의 bands 는 계열을
      // 그리는 차례에 칠해서, 값 계열을 먼저 선언하면 띠가 값 선을 덮어 범위 안의 값이 안
      // 보였다(실제로 그렇게 그려졌다).
      // 보는 구간(band·끄는 중인 구간)의 칠도 여기서 깐다 — 계열·범위 밖 점보다 먼저. draw 에서 칠하면
      // 데이터 위에 덮이는데, 라이트 테마의 --primary-subtle 은 불투명(#e7eefe)이라 구간 안의 선·빨간 점이
      // 통째로 가려 큰 파란 사각형만 보였다(eqm-rtdv 운영 화면, 2026-10-19). 테두리는 draw 에 남긴다.
      drawClear: [(u) => {
        drawSpanFill(u, propsRef.current, selRef.current, r)
        drawBands(u, propsRef.current, r)
        drawFrame(u, propsRef.current, r)
      }],
      draw: [(u) => drawExtras(u, propsRef.current, selRef.current, r)],
      setCursor: [(u) => reportHover(u, propsRef.current)],
    },
  }
}

function seriesOptions(s, i, preparedRef, propsRef, r) {
  const kind = s.kind || 'auto'
  const color = () => r.color(s.color)
  if (kind === 'line') return lineSeriesOptions(s, color)
  const meta = {
    kind,
    get bucketMs() { return propsRef.current.bucketMs ?? 60_000 },
    get base() { return preparedRef.current.bases[i] },
    get groupTop() { return preparedRef.current.groupTop[i] },
    colors: () => ({
      fill: r.color(s.color),
      over: s.over ? { value: s.over.value, fill: r.color(s.over.color) } : null,
    }),
  }
  return {
    label: s.label, scale: 'y', width: 0, fill: color, stroke: color, points: { show: false },
    paths: columnPaths(meta),
  }
}

// drawFrame 은 판(플롯 영역) 둘레에 테두리를 긋는다(frame). 축선이 없으면 값의 바닥(0)과 끝이
// 어디인지 안 보인다 — 배경만 회색인 판에서 0 근처를 기는 선은 바닥에 붙은 것인지 떠 있는 것인지
// 모른다(store-mops 리소스 추이, 2026-10-28).
//
// 선은 판 **가장자리 바로 바깥**에 긋는다. 안쪽에 그으면 테두리가 판 안 1px 을 먹어 0 값 선·최댓값
// 선을 덮는다(실화면 시안 1차에서 그랬다). 계열보다 먼저(drawClear) 그려 데이터가 늘 위에 온다.
function drawFrame(u, props, r) {
  if (!props.frame) return
  const { ctx, bbox } = u
  const pr = devicePixelRatioOf(u)
  const h = pr / 2
  ctx.save()
  ctx.strokeStyle = r.color('--border-control')
  ctx.lineWidth = pr
  ctx.setLineDash([])
  ctx.strokeRect(bbox.left - h, bbox.top - h, bbox.width + pr, bbox.height + pr)
  ctx.restore()
}

// drawExtras 는 series 위에 기준선과 고른 구간을 그린다. 좌표는 uPlot 의 valToPos(float64)로 옮긴다.
function drawExtras(u, props, selecting, r) {
  const { ctx, bbox } = u
  const pr = devicePixelRatioOf(u)
  ctx.save()
  ctx.beginPath()
  ctx.rect(bbox.left, bbox.top, bbox.width, bbox.height)
  ctx.clip()
  drawOutside(u, props, r, pr)
  for (const l of props.lines || []) {
    const yy = Math.round(u.valToPos(l.value, 'y', true)) + 0.5
    ctx.strokeStyle = r.color(l.color)
    ctx.lineWidth = pr
    ctx.setLineDash(l.dash ? [3 * pr, 3 * pr] : [])
    ctx.beginPath()
    ctx.moveTo(bbox.left, yy)
    ctx.lineTo(bbox.left + bbox.width, yy)
    ctx.stroke()
  }
  // 구간 테두리는 데이터 위에 그린다(칠은 drawClear 에서 데이터 아래에 깔았다 — drawSpanFill).
  const s = spanRect(u, selecting || props.band, pr)
  if (s) {
    ctx.setLineDash([])
    ctx.strokeStyle = r.color('--primary')
    ctx.lineWidth = pr
    ctx.strokeRect(s.x, bbox.top, s.w, bbox.height)
  }
  ctx.restore()
}

// 끄는 중이면 그 구간을, 아니면 확정된 구간을 그린다. 확정된 뒤에도 지우지 않는다 —
// 그 구간을 보고 있는 다른 표가 무엇을 보는지 차트에서 알 수 있어야 한다.
function spanRect(u, b, pr) {
  if (!b || !(b.to > b.from)) return null
  const x1 = u.valToPos(b.from, 'x', true)
  const x2 = u.valToPos(b.to, 'x', true)
  // 아주 좁게 확대해도 보이게 2px 아래로는 안 줄인다.
  return { x: x1, w: Math.max(2 * pr, x2 - x1) }
}

// 구간 칠 — 판을 지운 직후(drawClear), 계열보다 먼저 깐다. 색이 불투명한 테마에서도 데이터가 그 위에 보인다.
function drawSpanFill(u, props, selecting, r) {
  const s = spanRect(u, selecting || props.band, devicePixelRatioOf(u))
  if (!s) return
  const { ctx, bbox } = u
  ctx.save()
  ctx.beginPath()
  ctx.rect(bbox.left, bbox.top, bbox.width, bbox.height)
  ctx.clip()
  ctx.fillStyle = r.color('--primary-subtle')
  ctx.fillRect(s.x, bbox.top, s.w, bbox.height)
  ctx.restore()
}

// drawBands 는 `bands` 의 짝마다 위·아래 계열 사이를 칠한다. 기준선이 빈 행에서 끊는다
// (model.js 의 bandSegments). 좌표는 uPlot 의 valToPos(float64)로 옮긴다.
function drawBands(u, props, r) {
  const pairs = bandPairs(props.series, props.bands)
  if (!pairs.length) return
  const { ctx, bbox } = u
  const xs = u.data[0]
  const idxs = u.series[1]?.idxs || [0, xs.length - 1]
  ctx.save()
  ctx.beginPath()
  ctx.rect(bbox.left, bbox.top, bbox.width, bbox.height)
  ctx.clip()
  for (const b of pairs) {
    const up = props.data[props.series[b.upper].key]
    const lo = props.data[props.series[b.lower].key]
    ctx.fillStyle = r.color(b.color || '--primary-subtle')
    for (const seg of bandSegments(up, lo, idxs[0], idxs[1])) {
      ctx.beginPath()
      seg.forEach((k, n) => {
        const x = u.valToPos(xs[k], 'x', true)
        const y = u.valToPos(up[k], 'y', true)
        if (n === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      for (let n = seg.length - 1; n >= 0; n--) {
        const k = seg[n]
        ctx.lineTo(u.valToPos(xs[k], 'x', true), u.valToPos(lo[k], 'y', true))
      }
      ctx.closePath()
      ctx.fill()
    }
  }
  ctx.restore()
}

// drawOutside 는 `outside` 를 단 계열에서 기준선 밖의 점을 그 색으로 찍는다. 기준선은 data 의
// key 로 가리킨다(계열로 그리지 않은 칸도 된다). 무엇을 넘었다고 볼지는 model.js 의
// outsideIndices 가 정한다 — 그리기와 판단을 갈라 node 시험이 판단을 본다.
function drawOutside(u, props, r, pr) {
  const xs = u.data[0]
  const c = u.ctx
  props.series.forEach((s, i) => {
    if (!s.outside) return
    const vals = props.data[s.key]
    const idxs = u.series[i + 1]?.idxs || [0, xs.length - 1]
    const hits = outsideIndices(vals, props.data[s.outside.lower], props.data[s.outside.upper], idxs[0], idxs[1])
    if (!hits.length) return
    c.fillStyle = r.color(s.outside.color || '--danger')
    for (const k of hits) {
      c.beginPath()
      c.arc(u.valToPos(xs[k], 'x', true), u.valToPos(vals[k], 'y', true), 3 * pr, 0, Math.PI * 2)
      c.fill()
    }
  })
}

// reportHover 는 커서가 가리키는 **버킷**을 부르는 쪽에 알린다. 같은 syncKey 의 다른 판에서
// 커서가 움직여도 불린다 — 그래서 레인 하나에만 onHover 를 달면 된다.
function reportHover(u, props) {
  if (!props.onHover) return
  const left = u.cursor.left
  if (left == null || left < 0) { props.onHover(null, null); return }
  const t = u.posToVal(left, 'x')
  const idx = nearestIndex(u.data[0], t, props.bucketMs ?? 60_000)
  props.onHover(idx >= 0 ? idx : null, idx >= 0 ? u.data[0][idx] : null)
}

// attachInteractions 는 휠 확대·끌어 이동·Shift + 끌어 고르기·누르기를 판에 붙인다.
//
// 조작마다 켜는 조건이 있다: onWindowChange·extent 가 있어야 확대·이동, onSelect 가 있어야
// 고르기, onPick 이 있어야 누르기. 아무것도 안 넘기면 보기만 하는 판이다.
function attachInteractions(u, propsRef, selRef) {
  const over = u.over
  let drag = null
  // 개관 띠의 창을 끄는 중(onBandChange) — { mode, start, x }.
  let bandDrag = null
  const timeAt = (clientX) => u.posToVal(clientX - over.getBoundingClientRect().left, 'x')
  // 누른 자리가 창의 어디인가(model.js bandHit). 창을 끌 수 없는 판이면 null.
  const bandAt = (e) => {
    const p = propsRef.current
    if (!p.onBandChange || !p.band || !(p.band.to > p.band.from)) return null
    const px = e.clientX - over.getBoundingClientRect().left
    const edge = e.pointerType === 'touch' ? BAND_EDGE_TOUCH_PX : BAND_EDGE_PX
    return bandHit(px, u.valToPos(p.band.from, 'x'), u.valToPos(p.band.to, 'x'), edge)
  }

  function onDown(e) {
    const p = propsRef.current
    if (e.button !== 0) return
    over.setPointerCapture(e.pointerId)
    const hit = bandAt(e)
    if (hit) {
      bandDrag = { mode: hit, start: p.band, x: e.clientX, moved: 0 }
      return
    }
    if (e.shiftKey && p.onSelect) {
      const t = timeAt(e.clientX)
      selRef.current = { anchor: t, from: t, to: t }
      return
    }
    drag = { x: e.clientX, win: p.window, moved: 0 }
  }

  function onMove(e) {
    const p = propsRef.current
    over.classList.toggle('is-picking', !!(e.shiftKey && p.onSelect))
    over.classList.toggle('is-pannable', !!(p.onWindowChange && p.extent))
    if (bandDrag) {
      const span = (u.scales.x.max - u.scales.x.min) || 1
      bandDrag.moved = Math.max(bandDrag.moved, Math.abs(e.clientX - bandDrag.x))
      // 손 떨림까지 끌기로 보면 창 안을 톡 누르기(onPick)가 안 먹는다.
      if (bandDrag.moved <= DRAG_SLOP) return
      const dt = ((e.clientX - bandDrag.x) / (over.clientWidth || 1)) * span
      const bounds = p.window && p.window.to > p.window.from ? p.window : { from: u.scales.x.min, to: u.scales.x.max }
      p.onBandChange(dragBand(bandDrag.start, bandDrag.mode, dt, bounds, p.bandMinSpan ?? 0))
      return
    }
    // 창 위에서는 무엇을 끌게 되는지 커서가 말한다 — 가운데는 옮기기, 양끝은 폭 바꾸기.
    const hover = e.buttons ? null : bandAt(e)
    over.classList.toggle('is-band-move', hover === 'move')
    over.classList.toggle('is-band-edge', hover === 'from' || hover === 'to')
    const sel = selRef.current
    if (sel) {
      const t = timeAt(e.clientX)
      selRef.current = { anchor: sel.anchor, from: Math.min(sel.anchor, t), to: Math.max(sel.anchor, t) }
      u.redraw(false)
      return
    }
    if (!drag) return
    const dx = e.clientX - drag.x
    drag.moved = Math.max(drag.moved, Math.abs(dx))
    if (!p.onWindowChange || !p.extent || !drag.win) return
    const span = drag.win.to - drag.win.from
    const shift = (dx / (over.clientWidth || 1)) * span
    p.onWindowChange(clampWindow({ from: drag.win.from - shift, to: drag.win.to - shift }, p.extent))
  }

  function onUp(e) {
    const p = propsRef.current
    if (bandDrag) {
      // 끌지 않고 누르기만 했으면 예전처럼 그 자리로 옮긴다(onPick) — 창 안을 누른다고 아무 일도 없으면 고장으로 읽힌다.
      if (bandDrag.moved <= DRAG_SLOP && p.onPick) p.onPick(timeAt(e.clientX))
      bandDrag = null
      return
    }
    const sel = selRef.current
    if (sel) {
      selRef.current = null
      const span = (u.scales.x.max - u.scales.x.min) || 1
      // 픽셀로 잰다 — 시각으로 재면 확대 배율에 따라 「손이 떨린 것」의 크기가 달라진다.
      const px = ((sel.to - sel.from) / span) * over.clientWidth
      const unit = p.bucketMs ?? 60_000
      if (px > DRAG_SLOP) {
        // 버킷 하나보다 좁은 선택은 버킷 하나로 늘린다. 자료가 그 단위로만 있다.
        p.onSelect(sel.to - sel.from < unit ? { from: sel.from, to: sel.from + unit } : { from: sel.from, to: sel.to })
      }
      u.redraw(false)
      return
    }
    // 터치에는 마우스 움직임이 없어 uPlot 커서(기준선·onHover·툴팁)가 그대로 멈춰 있다 — 폰에서는
    // 툴팁이 아예 안 떴다. 톡 누른 자리로 커서를 옮기고 같은 syncKey 의 판에도 알린다(_pub).
    if (drag && drag.moved <= DRAG_SLOP && e.pointerType === 'touch') {
      const r = over.getBoundingClientRect()
      u.setCursor({ left: e.clientX - r.left, top: e.clientY - r.top }, true, true)
    }
    if (drag && drag.moved <= DRAG_SLOP && p.onPick) p.onPick(timeAt(e.clientX))
    drag = null
  }

  // 휠 확대는 기본 스크롤을 막아야 하는데 React 의 onWheel 은 passive 라 preventDefault 가
  // 안 먹는다. 그래서 직접 건다.
  function onWheel(e) {
    const p = propsRef.current
    if (!p.onWindowChange || !p.extent || !p.window) return
    e.preventDefault()
    const anchor = timeAt(e.clientX)
    p.onWindowChange(zoomWindow(p.window, p.extent, e.deltaY > 0 ? 1.25 : 0.8, anchor, p.bucketMs))
  }

  over.addEventListener('pointerdown', onDown)
  over.addEventListener('pointermove', onMove)
  over.addEventListener('pointerup', onUp)
  over.addEventListener('pointercancel', onUp)
  over.addEventListener('wheel', onWheel, { passive: false })
  return () => {
    over.removeEventListener('pointerdown', onDown)
    over.removeEventListener('pointermove', onMove)
    over.removeEventListener('pointerup', onUp)
    over.removeEventListener('pointercancel', onUp)
    over.removeEventListener('wheel', onWheel)
  }
}
